import React, { useState } from 'react';
import { FootHealthAssessment, FOOT_REGIONS } from '../FootHealthMonitoring';
import { ArrowLeft, ArrowRight, Activity, Download, AlertCircle } from 'lucide-react';

interface PressureDistributionMapProps {
  assessment: FootHealthAssessment;
  baselineAssessment?: FootHealthAssessment;
}

const PressureDistributionMap: React.FC<PressureDistributionMapProps> = ({ 
  assessment, 
  baselineAssessment 
}) => {
  const [activeFoot, setActiveFoot] = useState<'left' | 'right'>('right'); // Start with right foot as it shows the problem in our mock data
  const [view, setView] = useState<'current' | 'baseline' | 'comparison'>('current');
  
  // Get the active foot data
  const currentFootData = activeFoot === 'left' ? assessment.leftFootData : assessment.rightFootData;
  const baselineFootData = baselineAssessment ? 
    (activeFoot === 'left' ? baselineAssessment.leftFootData : baselineAssessment.rightFootData) : 
    undefined;
  
  // Calculate max pressure for scale normalization
  const getMaxPressure = () => {
    let max = 0;
    
    // Check current foot
    Object.values(currentFootData.regions).forEach(region => {
      region.pressurePoints.forEach(point => {
        if (point.pressure > max) max = point.pressure;
      });
    });
    
    // Check baseline foot if comparing
    if (baselineFootData && (view === 'baseline' || view === 'comparison')) {
      Object.values(baselineFootData.regions).forEach(region => {
        region.pressurePoints.forEach(point => {
          if (point.pressure > max) max = point.pressure;
        });
      });
    }
    
    return max;
  };
  
  // Helper to get pressure color
  const getPressureColor = (pressure: number, maxPressure: number) => {
    // Scale from 0 to 100
    const scaledPressure = (pressure / maxPressure) * 100;
    
    // Color scale from blue (low) to red (high)
    if (scaledPressure < 20) {
      return `rgba(0, 0, 255, ${scaledPressure / 20})`;
    } else if (scaledPressure < 40) {
      return `rgba(0, ${255 - ((scaledPressure - 20) * 12.75)}, 255, 1)`;
    } else if (scaledPressure < 60) {
      return `rgba(${(scaledPressure - 40) * 12.75}, 0, 255, 1)`;
    } else if (scaledPressure < 80) {
      return `rgba(255, 0, ${255 - ((scaledPressure - 60) * 12.75)}, 1)`;
    } else {
      return `rgba(255, ${(scaledPressure - 80) * 12.75}, 0, 1)`;
    }
  };
  
  // Get color for comparison view
  const getComparisonColor = (currentPressure: number, baselinePressure: number) => {
    // Calculate percent change
    const percentChange = ((currentPressure - baselinePressure) / baselinePressure) * 100;
    
    // Color based on change
    if (percentChange > 30) {
      return 'rgba(255, 0, 0, 0.8)'; // Much higher (red)
    } else if (percentChange > 10) {
      return 'rgba(255, 165, 0, 0.8)'; // Higher (orange)
    } else if (percentChange < -30) {
      return 'rgba(0, 0, 255, 0.8)'; // Much lower (blue)
    } else if (percentChange < -10) {
      return 'rgba(0, 191, 255, 0.8)'; // Lower (light blue)
    } else {
      return 'rgba(0, 128, 0, 0.8)'; // Similar (green)
    }
  };
  
  // Get the points to render based on the current view
  const getPointsToRender = () => {
    const maxPressure = getMaxPressure();
    
    if (view === 'current') {
      return currentFootData.regions;
    } else if (view === 'baseline' && baselineFootData) {
      return baselineFootData.regions;
    } else if (view === 'comparison' && baselineFootData) {
      // For comparison view, we need to combine the data
      const comparisonRegions = { ...currentFootData.regions };
      
      // For each region, calculate the difference
      Object.keys(comparisonRegions).forEach(regionId => {
        if (baselineFootData.regions[regionId]) {
          const currentRegion = currentFootData.regions[regionId];
          const baselineRegion = baselineFootData.regions[regionId];
          
          // We keep the same points but change the color based on the difference
          comparisonRegions[regionId] = {
            ...currentRegion,
            pressurePoints: currentRegion.pressurePoints.map(point => {
              // Find closest point in baseline for comparison
              const closestBaselinePoint = baselineRegion.pressurePoints.reduce((closest, bp) => {
                const currentDist = Math.sqrt(Math.pow(bp.x - point.x, 2) + Math.pow(bp.y - point.y, 2));
                const closestDist = Math.sqrt(Math.pow(closest.x - point.x, 2) + Math.pow(closest.y - point.y, 2));
                return currentDist < closestDist ? bp : closest;
              }, baselineRegion.pressurePoints[0]);
              
              return {
                ...point,
                baselineComparison: closestBaselinePoint.pressure
              };
            })
          };
        }
      });
      
      return comparisonRegions;
    }
    
    return currentFootData.regions;
  };
  
  const maxPressure = getMaxPressure();
  const regionsToRender = getPointsToRender();
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium">Foot Pressure Distribution</h3>
        <div className="flex space-x-3">
          <div className="flex items-center space-x-1 text-sm">
            <button
              onClick={() => setActiveFoot('left')}
              className={`px-3 py-1 rounded-l-md ${
                activeFoot === 'left' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Left Foot
            </button>
            <button
              onClick={() => setActiveFoot('right')}
              className={`px-3 py-1 rounded-r-md ${
                activeFoot === 'right' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Right Foot
            </button>
          </div>
          
          {baselineAssessment && (
            <div className="flex items-center space-x-1 text-sm">
              <button
                onClick={() => setView('current')}
                className={`px-3 py-1 rounded-l-md ${
                  view === 'current' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Current
              </button>
              <button
                onClick={() => setView('baseline')}
                className={`px-3 py-1 ${
                  view === 'baseline' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Baseline
              </button>
              <button
                onClick={() => setView('comparison')}
                className={`px-3 py-1 rounded-r-md ${
                  view === 'comparison' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Compare
              </button>
            </div>
          )}
          
          <button
            onClick={() => {}}
            className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-sm hover:bg-gray-50"
          >
            <Download size={16} className="mr-2" />
            Save Image
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pressure Map Visualization */}
        <div className="bg-gray-50 rounded-lg p-4 flex flex-col items-center">
          <div className="text-center mb-4">
            <h4 className="font-medium">
              {activeFoot === 'left' ? 'Left' : 'Right'} Foot - 
              {view === 'current' ? ' Current Assessment' : 
               view === 'baseline' ? ' Baseline Assessment' : 
               ' Comparison View'}
            </h4>
            <p className="text-sm text-gray-600">
              {view === 'comparison' 
                ? 'Red indicates increase in pressure, blue indicates decrease' 
                : 'Red indicates high pressure, blue indicates low pressure'}
            </p>
          </div>
          
          <div className="relative w-64 h-96 bg-white border border-gray-200 rounded-lg">
            {/* Foot outline */}
            <svg 
              viewBox="0 0 100 100" 
              className="absolute inset-0 w-full h-full"
              style={{ 
                transform: activeFoot === 'left' ? 'scale(-1, 1)' : 'none',
                opacity: 0.3 
              }}
            >
              <path 
                d="M50,10 C30,10 25,30 25,50 C25,65 30,80 50,90 C70,80 75,65 75,50 C75,30 70,10 50,10 Z" 
                fill="none" 
                stroke="gray" 
                strokeWidth="1"
              />
            </svg>
            
            {/* Render pressure points */}
            <svg 
              viewBox="0 0 100 100" 
              className="absolute inset-0 w-full h-full"
              style={{ transform: activeFoot === 'left' ? 'scale(-1, 1)' : 'none' }}
            >
              {Object.values(regionsToRender).flatMap(region => 
                region.pressurePoints.map((point, idx) => (
                  <circle 
                    key={idx}
                    cx={point.x} 
                    cy={point.y} 
                    r={3 + (point.pressure / maxPressure) * 7}
                    fill={
                      view === 'comparison' && 'baselineComparison' in point
                        ? getComparisonColor(point.pressure, (point as any).baselineComparison)
                        : getPressureColor(point.pressure, maxPressure)
                    }
                    opacity={0.7}
                  />
                ))
              )}
            </svg>
          </div>
          
          {/* Pressure scale */}
          <div className="mt-4 w-64">
            {view === 'comparison' ? (
              <div className="flex justify-between items-center text-xs">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-600 mr-1 rounded-sm"></div>
                  <span>Much Lower</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-600 mr-1 rounded-sm"></div>
                  <span>Similar</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-600 mr-1 rounded-sm"></div>
                  <span>Much Higher</span>
                </div>
              </div>
            ) : (
              <div>
                <div className="h-2 w-full bg-gradient-to-r from-blue-500 via-green-500 to-red-500 rounded-full"></div>
                <div className="flex justify-between text-xs mt-1">
                  <span>Low Pressure</span>
                  <span>High Pressure</span>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Regional Pressure Analysis */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-4">Regional Pressure Analysis</h4>
          
          <div className="space-y-3">
            {FOOT_REGIONS.map(region => {
              const currentRegionData = currentFootData.regions[region.id];
              const baselineRegionData = baselineFootData?.regions[region.id];
              
              const isAbnormal = currentRegionData.averagePressure < region.normalPressureRange[0] || 
                                currentRegionData.averagePressure > region.normalPressureRange[1];
              
              const changeFromBaseline = baselineRegionData
                ? ((currentRegionData.averagePressure - baselineRegionData.averagePressure) / 
                   baselineRegionData.averagePressure) * 100
                : 0;
              
              return (
                <div 
                  key={region.id}
                  className={`p-3 rounded-lg border ${
                    isAbnormal ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex justify-between">
                    <span className="font-medium">{region.name}</span>
                    <span className={isAbnormal ? 'text-red-600' : ''}>
                      {currentRegionData.averagePressure.toFixed(1)}
                    </span>
                  </div>
                  
                  <div className="relative h-2 w-full bg-gray-200 rounded-full mt-2">
                    {/* Normal range indicator */}
                    <div 
                      className="absolute h-2 bg-green-200 rounded-full"
                      style={{ 
                        left: `${(region.normalPressureRange[0] / 100) * 100}%`,
                        width: `${((region.normalPressureRange[1] - region.normalPressureRange[0]) / 100) * 100}%`
                      }}
                    ></div>
                    
                    {/* Current value marker */}
                    <div 
                      className="absolute h-4 w-2 bg-blue-600 -mt-1 rounded-full"
                      style={{ left: `${(currentRegionData.averagePressure / 100) * 100}%` }}
                    ></div>
                    
                    {/* Baseline value marker (if comparing) */}
                    {baselineRegionData && (
                      <div 
                        className="absolute h-4 w-2 bg-gray-600 -mt-1 rounded-full"
                        style={{ left: `${(baselineRegionData.averagePressure / 100) * 100}%` }}
                      ></div>
                    )}
                  </div>
                  
                  <div className="flex justify-between text-xs mt-1">
                    <span>0</span>
                    <span>100</span>
                  </div>
                  
                  {baselineRegionData && (
                    <div className="mt-2 text-xs">
                      <span className={`font-medium ${
                        Math.abs(changeFromBaseline) > 15 
                          ? changeFromBaseline > 0 ? 'text-red-600' : 'text-blue-600'
                          : 'text-gray-600'
                      }`}>
                        {changeFromBaseline > 0 ? '+' : ''}
                        {changeFromBaseline.toFixed(1)}% from baseline
                      </span>
                    </div>
                  )}
                  
                  {isAbnormal && (
                    <div className="mt-2 text-xs text-red-600">
                      Outside normal range ({region.normalPressureRange[0]}-{region.normalPressureRange[1]})
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Analysis Summary */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium mb-2">Analysis Summary</h4>
        <p className="text-sm text-gray-700">
          {activeFoot === 'right' && assessment.overallAssessment.plantarFasciitisRisk > 50 ? (
            <>
              The pressure distribution in your right foot shows significantly increased pressure in the 
              heel region paired with decreased pressure in the medial arch. This pattern is consistent 
              with early plantar fasciitis development. We recommend reviewing your footwear and considering 
              stretching exercises focused on the plantar fascia.
            </>
          ) : activeFoot === 'right' && assessment.overallAssessment.plantarFasciitisRisk > 30 ? (
            <>
              Your right foot is showing moderate changes in pressure distribution, with slightly elevated 
              heel pressure. This may indicate early signs of heel strain. Consider supportive footwear and monitor
              for any heel pain, particularly in the morning.
            </>
          ) : (
            <>
              The pressure distribution in your {activeFoot} foot appears to be within normal parameters.
              Continue to maintain good foot health by wearing supportive footwear and exercising regularly.
            </>
          )}
        </p>
        
        {currentFootData.alertFlags.highPressurePoints && (
          <div className="mt-3 p-3 bg-yellow-50 text-yellow-700 rounded-lg border border-yellow-200 flex items-start">
            <AlertCircle size={16} className="mr-2 flex-shrink-0 mt-0.5" />
            <span className="text-sm">
              High pressure points detected in the {activeFoot} foot. This may lead to discomfort or tissue damage over time.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PressureDistributionMap; 